import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useBusinessNumber } from '../../../../shared/hooks/useBusinessNumber.js';

interface PlanRow {
  readonly planId: string;
  readonly planCode: string;
  readonly planName: string;
  readonly planTier: string;
  readonly status: string;
  readonly billingFrequency: string;
  readonly basePrice: string;
  readonly currency: string;
}

const PLAN_TIERS = ['STARTER', 'PROFESSIONAL', 'ENTERPRISE', 'INDUSTRY', 'CORPORATE', 'GOVERNMENT', 'PARTNER', 'CUSTOM'];
const PLAN_STATUSES = ['DRAFT', 'PUBLISHED', 'DEPRECATED', 'ARCHIVED'];
const FREQUENCIES = ['MONTHLY', 'QUARTERLY', 'ANNUAL', 'ONE_TIME', 'CONSUMPTION'];
const PAGE = 25;

/**
 * M44 §7 Plan Management Console (§28). Commercial operators publish and
 * maintain the plan catalogue. Viewing needs M44_PLAN_VIEW; create / update
 * need M44_PLAN_MANAGE (SoD).
 */
export function PlanManagementConsole(): ReactNode {
  const { money } = useBusinessNumber();
  const { token } = useAuth();
  const [rows, setRows] = useState<PlanRow[]>([]);
  const [total, setTotal] = useState(0);
  const [tierFilter, setTierFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [planName, setPlanName] = useState('');
  const [planTier, setPlanTier] = useState('STARTER');
  const [billingFrequency, setBillingFrequency] = useState('MONTHLY');
  const [basePrice, setBasePrice] = useState('0');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (tierFilter) params.set('planTier', tierFilter);
    if (statusFilter) params.set('status', statusFilter);
    if (search.trim()) params.set('search', search.trim());
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: PlanRow[]; total: number }>(`/v1/billing/plans?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, tierFilter, statusFilter, search, page, reload]);

  const create = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!planName.trim()) { setActionError('A plan name is required.'); return; }
    try {
      await apiRequest('/v1/billing/plans', {
        method: 'POST',
        body: { planName: planName.trim(), planTier, billingFrequency, basePrice: Number(basePrice) || 0 },
        token,
      });
      setNotice('Plan created.');
      setPlanName('');
      setBasePrice('0');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setActionError('You are not authorised to manage plans (M44_PLAN_MANAGE required).');
      } else {
        setActionError((err as Error).message);
      }
    }
  };

  return (
    <main aria-label="plan-management-console">
      <h1>Plan Management Console</h1>

      <section aria-label="plan-create">
        <h2>Create Plan</h2>
        <form aria-label="plan-form" onSubmit={(e) => void create(e)}>
          <label>Name<input aria-label="field-plan-name" value={planName} onChange={(e) => setPlanName(e.target.value)} /></label>
          <label>Tier
            <select aria-label="field-plan-tier" value={planTier} onChange={(e) => setPlanTier(e.target.value)}>
              {PLAN_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label>Frequency
            <select aria-label="field-billing-frequency" value={billingFrequency} onChange={(e) => setBillingFrequency(e.target.value)}>
              {FREQUENCIES.map((f) => <option key={f} value={f}>{f}</option>)}
            </select>
          </label>
          <label>Base Price<input aria-label="field-base-price" value={basePrice} onChange={(e) => setBasePrice(e.target.value)} /></label>
          <button type="submit" aria-label="submit-plan">Create</button>
        </form>
        {actionError && <p role="alert" aria-label="plan-action-error">{actionError}</p>}
        {notice && <p aria-label="plan-notice">{notice}</p>}
      </section>

      <section aria-label="plan-filters">
        <label>Tier
          <select aria-label="filter-tier" value={tierFilter} onChange={(e) => { setTierFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {PLAN_TIERS.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>
        <label>Status
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {PLAN_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label>Search<input aria-label="filter-search" value={search} onChange={(e) => { setSearch(e.target.value); setPage(0); }} /></label>
      </section>

      {error && <p role="alert" aria-label="plan-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="plan-total">{total} plan(s).</p>
          <table aria-label="plan-table">
            <thead>
              <tr><th scope="col">Code</th><th scope="col">Name</th><th scope="col">Tier</th><th scope="col">Frequency</th><th scope="col">Price</th><th scope="col">Status</th></tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={6}>No plans.</td></tr>
              ) : rows.map((p) => (
                <tr key={p.planId} data-plan={p.planId}>
                  <td aria-label={`plan-code-${p.planId}`}>{p.planCode}</td>
                  <td>{p.planName}</td>
                  <td>{p.planTier}</td>
                  <td>{p.billingFrequency}</td>
                  <td>{p.currency} {money(Number(p.basePrice))}</td>
                  <td aria-label={`plan-status-${p.planId}`}>{p.status}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="plan-pagination">
            <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        </>
      )}
    </main>
  );
}
